import { compare } from 'compare-versions';
import {
  useCallback,
  useState,
  type FunctionComponent,
  type ReactNode,
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import SyncIcon from '@atlaskit/icon-lab/core/sync';
import type { SourceType } from '@trello/analytics-types';
import { Analytics, formatContainers } from '@trello/atlassian-analytics';
import { desktopVersion } from '@trello/config';
import { usePlannerAccountId } from '@trello/id-context';
import { Button } from '@trello/nachos/button';
import { showFlag } from '@trello/nachos/experimental-flags';
import {
  Popover,
  PopoverScreen,
  usePopover,
  type PopoverPlacementType,
} from '@trello/nachos/popover';
import { Tooltip } from '@trello/nachos/tooltip';
import { token } from '@trello/theme';

import { ConnectAccountPopoverScreen } from '../PlannerHeaderToolbar/CalendarsPopover/ConnectAccountPopoverScreen';
import { PlannerPopoverScreen } from '../PlannerHeaderToolbar/CalendarsPopover/PlannerPopoverScreen';
import { useWorkspaceAri } from '../useWorkspaceAri';

import * as styles from './ConnectCalendarAccountButton.module.less';

export const ConnectCalendarAccountButton: FunctionComponent<{
  buttonText?: ReactNode | string;
  onAfterAuth?: () => void;
  analyticsSource?: SourceType;
  popoverPlacement?: PopoverPlacementType;
}> = ({
  buttonText,
  onAfterAuth,
  analyticsSource,
  popoverPlacement = 'bottom-end',
}) => {
  const intl = useIntl();

  const { workspaceId } = useWorkspaceAri();
  const plannerAccountId = usePlannerAccountId();
  const { triggerRef, popoverProps, toggle, hide } = usePopover({
    initialScreen: PlannerPopoverScreen.ConnectAccount,
  });

  const [isAuthenticating, setIsAuthenticating] = useState(false);

  const setIsAuthenticatingState = useCallback(
    (authenticating: boolean) => {
      if (authenticating) {
        hide();
      }
      setIsAuthenticating(authenticating);
    },
    [hide],
  );

  // Auth does not work in desktop versions older than 2.15.5
  // in local development, desktopVersion is 0
  const isDesktopAuthSupported =
    desktopVersion === '0' ? true : compare(desktopVersion, '2.15.5', '>=');

  const onClick = useCallback(() => {
    Analytics.sendUIEvent({
      action: 'clicked',
      actionSubject: 'button',
      actionSubjectId: 'connectAccountButton',
      source: analyticsSource ?? 'plannerScreen',
      containers: formatContainers({
        workspaceId,
      }),
      attributes: {
        personalProductivity: 'planner',
        containerType: 'planner',
      },
    });
    if (isDesktopAuthSupported) {
      toggle();
    } else {
      showFlag({
        id: 'plannerDesktopAuthError',
        title: intl.formatMessage({
          id: 'templates.planner.update-desktop-message',
          defaultMessage:
            'Update to the latest version of Trello to use Planner.',
          description:
            'Text for flag letting the user know they need to update Trello to get the latest features.',
        }),
        appearance: 'error',
        isAutoDismiss: false,
      });
    }
  }, [isDesktopAuthSupported, toggle, workspaceId, intl, analyticsSource]);

  return (
    <>
      <Tooltip
        content={
          plannerAccountId &&
          intl.formatMessage({
            id: 'templates.planner.connected-calendars-button-tooltip',
            defaultMessage: 'View connected calendars',
            description: 'Tooltip for the planner connected calendars button',
          })
        }
      >
        <Button
          ref={triggerRef}
          onClick={onClick}
          iconBefore={
            <SyncIcon
              label={''}
              aria-hidden="true"
              color={token('color.icon.inverse', '#FFFFFF')}
              size="small"
            />
          }
          appearance={'primary'}
          isSelected={popoverProps.isVisible}
          isDisabled={isAuthenticating}
        >
          <div className={styles.connectButton}>
            {buttonText ?? (
              <FormattedMessage
                id="templates.planner.connect-an-account"
                defaultMessage="Connect an account"
                description="Connect an account button text"
              />
            )}
          </div>
        </Button>
      </Tooltip>
      <Popover
        enableArrowKeyNavigation
        {...popoverProps}
        placement={popoverPlacement}
      >
        <PopoverScreen
          id={PlannerPopoverScreen.ConnectAccount}
          title={intl.formatMessage({
            id: 'templates.planner.calendar-accounts',
            defaultMessage: 'Calendar accounts',
            description: 'Title for calendar account connection popover',
          })}
          noHorizontalPadding
        >
          <ConnectAccountPopoverScreen
            setIsAuthenticating={setIsAuthenticatingState}
            onAfterAuth={onAfterAuth}
            analyticsSource={analyticsSource}
          />
        </PopoverScreen>
      </Popover>
    </>
  );
};
